import { Formation, FormationPosition } from '@/components/team/FormationPitch';

/**
 * Dynamically generate a Formation from any schema string like "4-3-3", "5-3-2", "4-1-3-2", etc.
 *
 * Algorithm:
 * - GK is always at (50%, 88%)
 * - Parse schema into line sizes (e.g. "5-3-2" → [5, 3, 2])
 * - First line = DEF, last line = FWD, middle lines = MID
 * - Lines are distributed vertically from ~72% (defense) to ~20% (attack)
 * - Players within each line are spread evenly across the X axis with padding
 */
export function generateFormation(schema: string): Formation {
  const lineSizes = schema
    .replace(/\s+/g, '')
    .split('-')
    .map(Number)
    .filter((n) => !isNaN(n) && n > 0);

  if (lineSizes.length < 2) {
    // Fallback: return a basic 4-4-2 if schema is unparseable
    return generateFormation('4-4-2');
  }

  const positions: FormationPosition[] = [];

  // GK always fixed
  positions.push({ id: 'gk', label: 'GK', role: 'GK', xPercent: 50, yPercent: 88 });

  const numLines = lineSizes.length;

  // Y distribution: defense near bottom, attack near top
  // With 1 line → y=70; with N lines → evenly from 72 (defense) to 20 (attack)
  const yTop = 20;    // closest to attacking goal
  const yBottom = 72; // closest to own goal

  // Role labels for position IDs
  const roleAbbrev: Record<FormationPosition['role'], string> = {
    GK: 'gk',
    DEF: 'def',
    MID: 'mid',
    FWD: 'fwd',
  };

  // Track how many of each role we've created (for unique IDs)
  const roleCounts: Record<string, number> = { def: 0, mid: 0, fwd: 0 };

  for (let lineIdx = 0; lineIdx < numLines; lineIdx++) {
    const count = lineSizes[lineIdx];

    // Determine role: first line = DEF, last line = FWD, everything else = MID
    let role: FormationPosition['role'];
    if (lineIdx === 0) {
      role = 'DEF';
    } else if (lineIdx === numLines - 1) {
      role = 'FWD';
    } else {
      role = 'MID';
    }

    // Y position for this line
    // lineIdx 0 (defense) → yBottom, lineIdx numLines-1 (attack) → yTop
    const yPercent = numLines === 1
      ? (yTop + yBottom) / 2
      : yBottom - (lineIdx / (numLines - 1)) * (yBottom - yTop);

    // X positions: spread scales with line size so 2 strikers stay compact
    // but a back 5 stretches wide. Padding from touchlines shrinks as count grows.
    // count=2 → pad ~30 (positions at 30,70)  count=5 → pad ~14 (full width)
    let xPadding: number;
    if (count <= 2) xPadding = 30;
    else if (count <= 3) xPadding = 20;
    else xPadding = 14;
    for (let i = 0; i < count; i++) {
      const xPercent = count === 1
        ? 50
        : xPadding + (i / (count - 1)) * (100 - 2 * xPadding);

      const abbrev = roleAbbrev[role];
      const idx = roleCounts[abbrev]++;
      const id = `${abbrev}-${idx}`;

      // Generate a descriptive label
      const label = generateLabel(role, i, count);

      positions.push({
        id,
        label,
        role,
        xPercent: Math.round(xPercent * 10) / 10,
        yPercent: Math.round(yPercent * 10) / 10,
      });
    }
  }

  return { name: schema, positions };
}

/** Generate a human-readable label for a position based on role and lateral index. */
function generateLabel(role: FormationPosition['role'], index: number, total: number): string {
  if (role === 'GK') return 'GK';

  const labels: Record<FormationPosition['role'], { single: string; left: string; center: string; right: string; leftCenter: string; rightCenter: string }> = {
    GK:  { single: 'GK',  left: 'GK',  center: 'GK',  right: 'GK',  leftCenter: 'GK',  rightCenter: 'GK' },
    DEF: { single: 'CB',  left: 'LB',  center: 'CB',  right: 'RB',  leftCenter: 'LCB', rightCenter: 'RCB' },
    MID: { single: 'CM',  left: 'LM',  center: 'CM',  right: 'RM',  leftCenter: 'LCM', rightCenter: 'RCM' },
    FWD: { single: 'ST',  left: 'LW',  center: 'ST',  right: 'RW',  leftCenter: 'LST', rightCenter: 'RST' },
  };

  const l = labels[role];

  if (total === 1) return l.single;
  if (total === 2) {
    return index === 0 ? l.leftCenter : l.rightCenter;
  }
  if (total === 3) {
    if (index === 0) return l.left;
    if (index === 1) return l.center;
    return l.right;
  }
  // 4+: leftmost=left, rightmost=right, middle ones=center variants
  if (index === 0) return l.left;
  if (index === total - 1) return l.right;
  if (index < total / 2) return l.leftCenter;
  return l.rightCenter;
}

// Common formations to populate the dropdown on TeamSelectionPage
const FORMATION_NAMES = [
  '4-3-3', '4-2-3-1', '3-5-2', '4-4-2', '4-5-1', '3-4-3',
  '5-3-2', '5-4-1', '4-1-4-1', '4-1-3-2', '3-4-1-2',
];

export const FORMATIONS: Formation[] = FORMATION_NAMES.map(generateFormation);
